import L from 'leaflet'
import leafletCSS from 'leaflet/dist/leaflet.css'
import { css, html, LitElement, type PropertyValues, svg, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'

// Define the shape of our signals for Type Safety
interface MapView {
  lat: number
  lng: number
  zoom: number
}

interface Wind {
  speed: number
  direction: number // radians
}

interface Pin {
  id: string
  lat: number
  lng: number
  wind: Wind
  rain: number // drops/min
  temp: number // °C
  online: boolean
}

function tempEmoji(celsius: number) {
  if (celsius >= 40) {
    return '☠️'
  } else if (celsius >= 32) {
    return '🥵'
  } else if (celsius >= 24) {
    return '😊'
  } else if (celsius >= 16) {
    return '🙂'
  } else if (celsius >= 8) {
    return '😬'
  }
  return '🥶'
}

@customElement('sensor-map')
export class SensorMap extends LitElement {
  // Local reference to the Leaflet instance
  private map?: L.Map
  private markerLayers?: L.LayerGroup
  private _markerMap: Map<string, L.marker>
  private _resizeObserver?: ResizeObserver

  @property({ type: Number })
  lat = 0
  @property({ type: Number })
  long = 0
  @property({ type: Number })
  zoom = 0
  @property({ type: Array })
  sensors: Pin[] = []
  @property({ type: String, attribute: 'focused-sensor' })
  focusedSensor = ''

  static override styles = [
    unsafeCSS(leafletCSS),
    css`
      :host {
        display: block;
        height: 80dvh;
        position: relative;
        overflow: hidden;
        outline: lightslategrey solid 5px;
        border-radius: 10px;
        margin-block-start: 5dvh;
      }
      #map {
        height: 100%;
        width: 100%;
      }
      .sensor {
        transition: transform 100ms ease-in-out;
        transform: scale(2);
        display: flex;
        background: color-mix(lightsteelblue 60%, transparent);
        border-radius: 20px;
        padding-inline: 2px;
        border: solid 2px blue;
        &:hover {
          transform: scale(3);
          background: color-mix(lightsteelblue 90%, transparent);
        }
      }
      .overlay {
        position: absolute;
        top: 10px;
        left: 50px;
        z-index: 1000;
        background: white;
        padding: 8px 12px;
        border-radius: 4px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        font-family: sans-serif;
      }
    `,
  ]

  protected constructor() {
    super()
    this._markerMap = new Map()
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this._resizeObserver?.disconnect()
    this.map?.remove()
  }

  protected override firstUpdated(): void {
    const mapContainer = this.renderRoot.querySelector('#map') as HTMLElement

    // Initialize Map
    this.map = L.map(mapContainer, { doubleClickZoom: false }).setView([this.lat, this.long], this.zoom)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
    }).addTo(this.map)

    this.markerLayers = L.layerGroup().addTo(this.map)

    // Invalidate size once the element is fully painted so Leaflet
    // picks up the correct dimensions from the start
    requestAnimationFrame(() => this.map?.invalidateSize())

    // Invalidate size whenever the host element is resized (e.g. style attribute changes)
    this._resizeObserver = new ResizeObserver(() => this.map?.invalidateSize())
    this._resizeObserver.observe(this)

    // Sync Map -> Signals
    this.map.on('moveend', () => {
      if (!this.map) return
      const center = this.map.getCenter()
      this.dispatchEvent(
        new CustomEvent('mapUpdate', {
          detail: {
            lat: center.lat,
            long: center.long,
            zoom: this.map.getZoom(),
          },
        }),
      )
      // this.dsRoot.view = {
      //   lat: center.lat,
      //   lng: center.lng,
      //   zoom: this.map.getZoom(),
      // }
    })

    // Interaction: Double Click -> Add Client Pin
    // this.map.on('dblclick', (e: L.LeafletMouseEvent) => {
    //   const newPin: Pin = { lat: e.latlng.lat, lng: e.latlng.lng }
    //   const currentPins = this.dsRoot.clientPins as Pin[]
    //   this.dsRoot.clientPins = [...currentPins, newPin]
    // })
  }

  protected override updated(changedProperties: PropertyValues<this>): void {
    if (!this.map || !this.markerLayers) return

    // 1. Sync View (Signal -> Map)
    const currentCenter = this.map.getCenter()
    if (currentCenter.lat !== this.lat || currentCenter.lng !== this.long) {
      this.map.setView([this.lat, this.long], this.zoom, { animate: true })
    }

    if (changedProperties.has('sensors') || changedProperties.has('focusedSensor')) {
      if (this.sensors) {
        this.syncMarkers(this.sensors)
      }
    }
  }

  private syncMarkers(sensors: Pin[]) {
    if (!sensors) return
    const incomingIDs = new Set(sensors.map((i) => i.id))
    for (const [id, marker] of this._markerMap) {
      if (!incomingIDs.has(id)) {
        marker.remove()
        this._markerMap.delete(id)
      }
    }

    for (const sensor of sensors) {
      // console.log(sensor.id, this._markerMap, this._markerMap.has(sensor.id))
      if (this._markerMap.has(sensor.id)) {
        this._updateMarker(sensor)
      } else {
        this._addMarker(sensor)
      }
    }
  }
  private _addMarker(sensor: Pin) {
    const marker = L.marker([sensor.lat, sensor.lng], {
      icon: this.makeIcon(sensor),
      color: '#3b82f6',
      fillOpacity: 0.8,
      radius: 8,
    }).addTo(this.markerLayers)
    marker.on('mouseover', function () {
      this.setZIndexOffset(9999)
    })
    marker.on('mouseout', function () {
      this.setZIndexOffset(0)
    })
    marker.on('click', () => {
      this.dispatchEvent(
        new CustomEvent('sensor-selected', {
          detail: { id: sensor.id, lat: sensor.lat, lng: sensor.lng },
          bubbles: true,
          composed: true,
        }),
      )
    })
    this._markerMap.set(sensor.id, marker)
  }

  private _updateMarker(sensor: Pin) {
    const marker: L.marker = this._markerMap.get(sensor.id)
    const pos = marker.getLatLng()

    if (pos.lat !== sensor.lat || pos.lng !== sensor.lng) {
      marker.setLatLng([sensor.lat, sensor.lng])
    }
    marker.setIcon(this.makeIcon(sensor))
  }

  private makeIcon(p: Pin) {
    const isFocused = this.focusedSensor !== '' && p.id === this.focusedSensor
    const borderColor = isFocused ? 'gold' : p.online ? 'lightseagreen' : 'indianred'
    const borderWidth = isFocused ? '4px' : '2px'
    return L.divIcon({
      html: `<div class="sensor" style="border-color: ${borderColor}; border-width: ${borderWidth};">
      ${
        p.rain > 0
          ? `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 128 128">
          <g id="rain__rain" clip-path="url(#rain__clip0_2045_39587)">
              <g id="rain__Sky">
                  <g id="rain__Clouds">
                      <g id="rain__Cloud">
                          <path id="rain__Cloud_2" fill="#e6effc" d="M90.389 63.086c2.295-9.204-2.648-18.95-11.38-22.604-8.744-3.658-19.181-.344-24.172 7.73-4.3-2.587-9.862-2.434-14.013.387-4.14 2.814-6.324 7.915-5.474 12.855-6.957 1.315-11.957 7.885-11.289 14.954S30.871 89.002 37.953 89h53.024C98.045 89 104 83.119 104 76.028c0-7.327-6.323-13.26-13.611-12.942"/>
                      </g>
                      <animateTransform attributeName="transform" begin="0s" calcMode="spline" dur="3s" keySplines=".42 0 .58 1; .42 0 .58 1" repeatCount="indefinite" type="translate" values="0 0;0 -3;0 0"/>
                  </g>
              </g>
              <g id="rain__Precipitation">
                  <g id="rain__Raindrops">
                      <path id="rain__Raindrop 1" stroke="#0a5ad4" stroke-linecap="round" stroke-width="4" d="M52 83v12">
                          <animateTransform attributeName="transform" begin="0s" calcMode="spline"
                          dur="${50 / p.rain}s"
                          keySplines=".42 0 1 1" repeatCount="indefinite" type="translate" values="0 0;0 20"/>
                          <animate attributeName="opacity" begin="0s"
                          dur="${50 / p.rain}s"
                          keyTimes="0;0.15;0.85;1" repeatCount="indefinite" values="0;1;1;0"/>
                      </path>
                      <path id="rain__Raindrop 2" stroke="#0a5ad4" stroke-linecap="round" stroke-width="4" d="M64 83v12">
                          <animateTransform attributeName="transform" begin="-0.6s" calcMode="spline"
                          dur="${50 / p.rain}s"
                          keySplines=".42 0 1 1" repeatCount="indefinite" type="translate" values="0 0;0 20"/>
                          <animate attributeName="opacity" begin="-0.6s"
                          dur="${50 / p.rain}s"
                          keyTimes="0;0.15;0.85;1" repeatCount="indefinite" values="0;1;1;0"/>
                      </path>
                      <path id="rain__Raindrop 3" stroke="#0a5ad4" stroke-linecap="round" stroke-width="4" d="M76 83v12">
                          <animateTransform attributeName="transform" begin="-0.19999999999999996s" calcMode="spline"
                          dur="${50 / p.rain}s"
                          keySplines=".42 0 1 1" repeatCount="indefinite" type="translate" values="0 0;0 20"/>
                          <animate attributeName="opacity" begin="-0.19999999999999996s"
                          dur="${50 / p.rain}s"
                          keyTimes="0;0.15;0.85;1" repeatCount="indefinite" values="0;1;1;0"/>
                      </path>
                  </g>
              </g>
          </g>
          <defs>
              <clipPath id="rain__clip0_2045_39587">
                  <rect width="128" height="128" fill="#fff"/>
              </clipPath>
          </defs>
      </svg>`
          : `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 128 128">
            <g id="clear-day__clear-day">
                <g id="clear-day__Sun">
                    <circle id="clear-day__Core" cx="64" cy="64" r="19.5" fill="url(#clear-day__paint0_linear_1802_5186)" stroke="#f8af18"/>
                    <g id="clear-day__Rays">
                        <path fill="#f8af18" d="M61 19a3 3 0 1 1 6 0v14a3 3 0 0 1-6 0zM93.699 30.059A3 3 0 1 1 97.94 34.3l-9.9 9.9a3 3 0 1 1-4.242-4.243zM109 61a3 3 0 1 1 0 6H95a3 3 0 1 1 0-6zM97.941 93.699a3 3 0 1 1-4.243 4.242l-9.899-9.9a3 3 0 1 1 4.243-4.242zM61 95a3 3 0 1 1 6 0v14a3 3 0 1 1-6 0zM39.958 83.799a3 3 0 1 1 4.243 4.243l-9.9 9.9a3 3 0 1 1-4.242-4.243zM33 61a3 3 0 1 1 0 6H19a3 3 0 0 1 0-6zM44.201 39.958a3 3 0 1 1-4.243 4.243l-9.9-9.9a3 3 0 1 1 4.243-4.242z"/>
                        <animateTransform attributeName="transform" begin="0s" dur="6s" repeatCount="indefinite" type="rotate" values="0 64.0 64.0;360 64.0 64.0"/>
                    </g>
                </g>
            </g>
            <defs>
                <linearGradient id="clear-day__paint0_linear_1802_5186" x1="64" x2="64" y1="44" y2="84" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#fbbf24"/>
                    <stop offset="1" stop-color="#f8af18"/>
                </linearGradient>
            </defs>
        </svg>
`
      }
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 128 128" style="transform: rotate(${p.wind.direction - 90}deg)">
          <g id="wind__wind">
              <g id="wind__Wind">
                  <path id="wind__Wind Line 1" stroke="black" stroke-dasharray="50" stroke-linecap="round" stroke-miterlimit="10"
                  stroke-width="10"
                  d="M87.79 40.135c6.186-6.788 17.21-1.73 17.21 7.311C105 53.275 100.522 58 95 58H24">
                      <animate attributeName="stroke-dashoffset" begin="0s" dur="${p.wind.speed / 10}s" repeatCount="indefinite" values="0;1000"/>
                  </path>
                  <path id="wind__Wind Line 1_2" stroke="black" stroke-dasharray="50" stroke-linecap="round" stroke-miterlimit="10"
                  stroke-width="10"
                  d="M60.128 87.868C66.42 94.518 78 89.872 78 80.556 78 74.726 73.35 70 67.615 70H24">
                      <animate attributeName="stroke-dashoffset" begin="-5.8s" dur="${p.wind.speed / 10}s" repeatCount="indefinite" values="0;1000"/>
                  </path>
              </g>
          </g>
      </svg>
      <div>${tempEmoji(p.temp)}</div>
      </div>
      `,
      className: '',
      iconSize: [50, 50],
      iconAnchor: [16, 42],
      popupAnchor: [0, -42],
    })
  }

  override render() {
    return html`<!--  <pre>${this.sensors === undefined ? 'undefined' : JSON.stringify(this.sensors)}</pre>-->
      <div id="map"></div>`
  }
}

// Global declaration for TS to recognize the tag in JSX/Templates
declare global {
  interface HTMLElementTagNameMap {
    'sensor-map': SensorMap
  }
}
